<template>
    <div class="customer-contacts-input">
        <div v-if="loading">
            <i class="fad fa-soinner fa-spin"></i> Chargement des contacts...
        </div>
        <div v-else-if="errors">
            <ErrorsZone :errors="errors" />
        </div>
        <template v-else>
            <div class="associated-contacts mb-2">
                <ul v-if="associatedContacts.length" class="list-group">
                    <li v-for="contact in associatedContacts" :key="contact.id" class="list-group-item contact">
                        <button type="button" class="btn btn-danger float-right" @click="dissociateContactFromCustomer(contact)">
                            <i class="fad fa-times"></i> Retirer
                        </button>
                        <input type="hidden" :name="name + '[]'" :value="contact.id">

                        <div>
                            <i class="fad fa-fw fa-user"></i> {{ contact.firstname }} {{ contact.lastname }}
                        </div>
                        <div v-if="contact.tel">
                            <i class="fad fa-fw fa-phone-square text-success"></i>
                            Téléphone : {{ contact.tel }}
                        </div>
                        <div v-if="contact.mobile">
                            <i class="fad fa-fw fa-mobile-alt text-primary"></i>
                            Mobile : {{ contact.mobile }}
                        </div>
                        <div v-if="contact.email">
                            <i class="fad fa-fw fa-envelope-open-text text-danger"></i>
                            Email : {{ contact.email}}
                            <span v-if="contact.emails_opt_out" class="text-danger small">
                                <i class="fas fa-fw fa-do-not-enter"></i> Opposé aux emails
                            </span>
                        </div>

                        <div class="btn-group mt-2" role="group">
                            <button type="button" :class="['btn btn-sm', mainContactId === contact.id ? 'btn-secondary': 'btn-outline-secondary']" @click="setMainContact(contact)">Principal</button>
                            <button type="button" :class="['btn btn-sm', billingContactId === contact.id ? 'btn-secondary': 'btn-outline-secondary']" @click="setBillingContact(contact)">Facturation</button>
                            <button type="button" :class="['btn btn-sm', dunningContactId === contact.id ? 'btn-secondary': 'btn-outline-secondary']" @click="setDunningContact(contact)">Relance</button>
                        </div>
                    </li>
                </ul>
                <div v-else class="alert alert-info">
                    <i class="fad fa-info-circle"></i> Aucun contact associé
                </div>
            </div>
            <input type="hidden" :name="name + '_main_contact_id'" :value="mainContactId">
            <input type="hidden" :name="name + '_billing_contact_id'" :value="billingContactId">
            <input type="hidden" :name="name + '_dunning_contact_id'" :value="dunningContactId">
            <div class="row">
                <div class="col-12 d-flex justify-content-between">
                    <button type="button" class="btn btn-secondary mr-2" @click="loadContacts">
                        <i class="fad fa-refresh"></i>
                    </button>
                    <v-select
                        class="flex-grow-1 mr-2"
                        :placeholder="placeholder"
                        :clearable="true"
                        :filterable="true"
                        :filter="fuseSearchFilter"
                        :options="contactsNotAssociated"
                        :get-option-label="(option) => option.name"
                        v-model="selectedContact"
                    >
                        <template #no-options>
                            Aucun contact ne correspond à votre recherche
                        </template>
                        <template #option="contact" class="border-bottom">
                            <div>
                                <i class="fad fa-fw fa-user"></i> {{ contact.firstname }} {{ contact.lastname }}
                            </div>
                            <div v-if="contact.tel">
                                <i class="fad fa-fw fa-phone-square text-success"></i>
                                Téléphone : {{ contact.tel }}
                            </div>
                            <div v-if="contact.mobile">
                                <i class="fad fa-fw fa-mobile-alt text-primary"></i>
                                Mobile : {{ contact.mobile }}
                            </div>
                            <div v-if="contact.email">
                                <i class="fad fa-fw fa-envelope-open-text text-danger"></i>
                                Email : {{ contact.email}}
                                <span v-if="contact.emails_opt_out" class="text-danger small">
                                <i class="fas fa-fw fa-do-not-enter"></i> Opposé aux emails
                            </span>
                            </div>
                        </template>
                    </v-select>
                    <button type="button" class="btn btn-primary mr-3" @click="associateSelectedContactToCustomer">
                        <i class="fad fa-plus"></i> Ajouter
                    </button>
                    <CreateContactModal @contactCreated="addCreatedContact"></CreateContactModal>
                    <a href="/espace-admin/contacts" target="_blank" class="btn btn-secondary">
                        <i class="fad fa-address-book"></i> Gestion des contacts <i class="fad fa-up-right-from-square"></i>
                    </a>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
    import CreateContactModal from "./CreateContactModal";
    import ErrorsZone from "../ErrorsZone";
    import Fuse from "fuse.js";
    import vSelect from 'vue-select';
    import {useGetLoader} from "../compositions/axios/loadingRequest";

    export default {
        name: "CustomerContactsInput",

        components: {
            CreateContactModal,
            ErrorsZone,
            vSelect,
        },

        props: {
            name: {
                type: String,
                required: true,
            },
            selectedContacts: {
                type: Array,
                required: false,
            },
            selectedMainContact: {
                type: Number,
                required: false,
            },
            selectedBillingContact: {
                type: Number,
                required: false,
            },
            selectedDunningContact: {
                type: Number,
                required: false,
            },
            placeholder: {
                type: String,
                required: false,
            },
        },

        setup() {
            const {
                loaded,
                loading,
                errors,
                load,
            } = useGetLoader();

            return {
                loaded,
                loading,
                errors,
                load,
            };
        },

        data: function () {
            return {
                contacts: [],
                selectedContact: null,
                mainContactId: null,
                billingContactId: null,
                dunningContactId: null,
                associatedContacts: [],
            };
        },

        async mounted() {
            await this.loadContacts();

            this.associatedContacts = [];
            this.mainContactId = null;
            this.billingContactId = null;
            this.dunningContactId = null;

            if (this.selectedContacts) {
                for (const contactId of this.selectedContacts) {
                    const contactFoundIndex = _.findIndex(this.contacts, {'id': contactId});
                    if (contactFoundIndex === -1) {
                        continue;
                    }

                    const contactFound = this.contacts[contactFoundIndex];

                    this.associatedContacts.push(contactFound);

                    if (this.selectedMainContact === contactFound.id) {
                        this.mainContactId = contactFound.id;
                    }
                    if (this.selectedBillingContact === contactFound.id) {
                        this.billingContactId = contactFound.id;
                    }
                    if (this.selectedDunningContact === contactFound.id) {
                        this.dunningContactId = contactFound.id;
                    }
                }
            }
        },

        watch: {
            associatedContacts(newValue) {
                if (newValue.length > 0) {
                    if (!this.mainContactId || !newValue.some((contact) => contact.id === this.mainContactId)) {
                        this.mainContactId = newValue[0].id;
                    }
                    if (!this.billingContactId || !newValue.some((contact) => contact.id === this.billingContactId)) {
                        this.billingContactId = newValue[0].id;
                    }
                    if (!this.dunningContactId || !newValue.some((contact) => contact.id === this.dunningContactId)) {
                        this.dunningContactId = newValue[0].id;
                    }
                } else {
                    this.mainContactId = this.billingContactId = this.dunningContactId = null;
                }
            },
        },

        computed: {
            contactsNotAssociated() {
                return this.contacts.filter((c) => _.findIndex(this.associatedContacts, {id: c.id}) === -1)
            },
        },

        methods: {
            addCreatedContact(contact) {
                this.associatedContacts.push(contact);
            },

            async loadContacts() {
                const data = await this.load(`/espace-admin/contacts`);
                if (data && data.contacts) {
                    this.contacts = data.contacts;
                } else {
                    this.contacts = [];
                }
            },

            setMainContact(contact) {
                this.mainContactId = contact.id;
            },

            setBillingContact(contact) {
                this.billingContactId = contact.id;
            },

            setDunningContact(contact) {
                this.dunningContactId = contact.id;
            },

            associateSelectedContactToCustomer() {
                if (this.selectedContact) {
                    this.associatedContacts.push(this.selectedContact);
                    this.selectedContact = null;
                }
            },

            dissociateContactFromCustomer(contact) {
                this.associatedContacts = this.associatedContacts.filter((c) => c.id !== contact.id);
            },

            fuseSearchFilter (options, search) {
                const fuse = new Fuse(options, {
                    keys: ['lastname', 'firstname'],
                    shouldSort: true,
                    includeScore: true,
                });
                let fuseList = search.length ? fuse.search(search) : fuse.list;
                let filtered = [];
                for (const key in fuseList) {
                    filtered.push(fuseList[key].item);
                }
                return filtered;
            },
        },
    }
</script>

<style scoped>
::v-deep .vs__dropdown-option {
    padding: 7px 20px;
    box-shadow: 0 1px #dddddd;
}
</style>
